import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Tag,
  Button,
  Drawer,
  Input,
  Space,
  notification,
  Segmented,
  Select,
  Popconfirm,
  Checkbox,
  Progress,
  Tooltip,
  Spin,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  FileTextOutlined,
  ThunderboltOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { fetchTemplates, createTemplate, updateTemplate, fetchVerifiedNumbers, sendTestTemplateMessage } from "./service";
import {
  PERSONA_OPTIONS,
  SUPPORTED_LANGUAGES,
  DEFAULT_LANGUAGE,
  getLanguageLabel,
  templateChannelUi,
  templateChannelApi,
  TEST_CALL_MAX_PER_HOUR,
} from "./constants";
import { languageForUi } from "./languageUtils";
import { markSetupTestComplete } from "./SetupTab";
import {
  estimateSpeechDuration,
  exceedsGreetingLimit,
  GREETING_MAX_SECONDS,
} from "./speechDuration";
import styles from "./style.module.scss";

const { TextArea } = Input;

const WHATSAPP_VARS = [
  "customer_name",
  "brand",
  "order_id",
  "discount_code",
];

const PREVIEW_VALUES = {
  customer_name: "Alex",
  brand: "Pivony",
  order_id: "ORD-4821",
  discount_code: "SAVE10",
  product_name: "Sneakers",
  agent_name: "Sonic",
};

const fillPreview = (text) =>
  (text || "").replace(/\{\{(\w+)\}\}/g, (_, key) => PREVIEW_VALUES[key] || `[${key}]`);

const TemplatesTab = ({ active, focusTest }) => {
  const { t, i18n } = useTranslation();
  const testCardRef = useRef(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [channel, setChannel] = useState("whatsapp");
  const [body, setBody] = useState("");
  const [language, setLanguage] = useState(() => languageForUi(i18n.language));
  const [persona, setPersona] = useState(PERSONA_OPTIONS[0].value);
  const [saving, setSaving] = useState(false);
  const bodyRef = useRef(null);

  const [numbers, setNumbers] = useState([]);
  const [testTemplateId, setTestTemplateId] = useState(null);
  const [testPhone, setTestPhone] = useState(undefined);
  const [testConsent, setTestConsent] = useState(false);
  const [testSending, setTestSending] = useState(false);
  const [quota, setQuota] = useState({
    test_calls_used: 0,
    test_calls_max: TEST_CALL_MAX_PER_HOUR,
    test_calls_remaining: TEST_CALL_MAX_PER_HOUR,
  });

  const load = useCallback(async () => {
    setLoading(true);
    const [tplRes, numbersRes] = await Promise.all([
      fetchTemplates(),
      fetchVerifiedNumbers(),
    ]);
    if (tplRes && !tplRes.error) {
      const list = tplRes.templates || [];
      setTemplates(list);
      const wa = list.filter((tpl) => !tpl.channel || tpl.channel === "whatsapp");
      setTestTemplateId((prev) => {
        if (prev && wa.some((tpl) => tpl.id === prev)) return prev;
        return wa[0]?.id ?? null;
      });
    }
    if (numbersRes && !numbersRes.error) {
      setNumbers(numbersRes.numbers || []);
      if (numbersRes.test_calls_remaining !== undefined) {
        setQuota({
          test_calls_used: numbersRes.test_calls_used,
          test_calls_max: numbersRes.test_calls_max,
          test_calls_remaining: numbersRes.test_calls_remaining,
        });
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (active) load();
  }, [active, load]);

  useEffect(() => {
    if (!active || !focusTest || !testCardRef.current) return undefined;
    const timer = setTimeout(() => {
      testCardRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 200);
    return () => clearTimeout(timer);
  }, [active, focusTest]);

  const whatsappTemplates = templates.filter(
    (tpl) => !tpl.channel || tpl.channel === "whatsapp"
  );

  const sendTestMessage = async () => {
    if (!testTemplateId || !testPhone || !testConsent) {
      notification.warning({ message: t("sonic.test_message_missing_fields") });
      return;
    }
    setTestSending(true);
    const res = await sendTestTemplateMessage({
      template_id: testTemplateId,
      phone: testPhone,
      consent: testConsent,
    });
    setTestSending(false);
    if (res && res.test_calls_remaining !== undefined) {
      setQuota({
        test_calls_used: res.test_calls_used,
        test_calls_max: res.test_calls_max,
        test_calls_remaining: res.test_calls_remaining,
      });
    }
    if (res && !res.error && res.success !== false) {
      notification.success({ message: t("sonic.test_message_sent") });
      markSetupTestComplete();
    } else {
      notification.error({
        message: (res && res.error) || t("sonic.test_message_failed"),
      });
    }
  };

  const openCreate = () => {
    setEditing(null);
    setName("");
    setChannel("whatsapp");
    setBody("");
    setLanguage(languageForUi(i18n.language));
    setPersona(PERSONA_OPTIONS[0].value);
    setDrawerOpen(true);
  };

  const openEdit = (tpl) => {
    setEditing(tpl);
    setName(tpl.name);
    setChannel(templateChannelUi(tpl.channel));
    setLanguage(tpl.language || DEFAULT_LANGUAGE);
    setBody(tpl.body || "");
    setPersona(tpl.persona || PERSONA_OPTIONS[0].value);
    setDrawerOpen(true);
  };

  const insertMergeField = (field) => {
    const token = `{{${field}}}`;
    const el =
      bodyRef.current && bodyRef.current.resizableTextArea
        ? bodyRef.current.resizableTextArea.textArea
        : null;
    if (el && typeof el.selectionStart === "number") {
      const start = el.selectionStart;
      const end = el.selectionEnd;
      setBody((b) => b.slice(0, start) + token + b.slice(end));
    } else {
      setBody((b) => b + token);
    }
  };

  const save = async () => {
    if (!name.trim() || !body.trim() || !language) {
      notification.warning({ message: t("sonic.template_fields_required") });
      return;
    }
    setSaving(true);
    const payload = {
      name: name.trim(),
      body: body.trim(),
      channel: templateChannelApi(channel),
      language,
      persona: channel === "voice" ? persona : undefined,
    };
    const res = editing
      ? await updateTemplate(editing.id, payload)
      : await createTemplate(payload);
    setSaving(false);
    if (res && !res.error) {
      notification.success({
        message: editing ? t("sonic.template_updated") : t("sonic.template_created"),
      });
      setDrawerOpen(false);
      load();
    } else {
      notification.error({ message: (res && res.error) || t("sonic.action_failed") });
    }
  };

  const handleDelete = async (tpl) => {
    notification.info({ message: t("sonic.template_delete_unavailable") });
  };

  const charCount = body.length;
  const voiceDuration = estimateSpeechDuration(body, language);
  const voiceTooLong = channel === "voice" && exceedsGreetingLimit(body, language);
  const quotaPct =
    quota.test_calls_max > 0
      ? Math.round((quota.test_calls_used / quota.test_calls_max) * 100)
      : 0;

  const mainContent = !loading && templates.length === 0 ? (
    <div className={styles.emptyState}>
      <FileTextOutlined className={styles.emptyStateIcon} />
      <h3>{t("sonic.templates_empty_title")}</h3>
      <p>{t("sonic.templates_empty_body")}</p>
      <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
        {t("sonic.create_template_cta")}
      </Button>
    </div>
  ) : (
    <>
      <div className={styles.sectionHeader}>
        <h3>{t("sonic.templates_title")}</h3>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          {t("sonic.new_template")}
        </Button>
      </div>

      <div className={styles.templateGrid}>
        {templates.map((tpl) => (
          <div key={tpl.id} className={styles.templateCard}>
            <div className={styles.templateCardTop}>
              <span className={styles.templateCardName}>{tpl.name}</span>
              <Space size={4}>
                <Tag>{getLanguageLabel(tpl.language || DEFAULT_LANGUAGE)}</Tag>
                <Tag color={tpl.channel === "voice_call" || tpl.channel === "voice" ? "purple" : "green"}>
                  {tpl.channel === "voice_call" || tpl.channel === "voice"
                    ? t("sonic.template_channel_voice")
                    : t("sonic.template_channel_whatsapp")}
                </Tag>
              </Space>
            </div>
            <p className={styles.templateCardPreview}>
              {(tpl.body || "").split("\n").slice(0, 2).join("\n")}
            </p>
            <div className={styles.templateCardActions}>
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={() => openEdit(tpl)}
              >
                {t("sonic.edit")}
              </Button>
              <Popconfirm
                title={t("sonic.confirm_delete_template")}
                onConfirm={() => handleDelete(tpl)}
                okText={t("sonic.delete")}
                cancelText={t("sonic.cancel")}
              >
                <Button type="text" size="small" danger icon={<DeleteOutlined />}>
                  {t("sonic.delete")}
                </Button>
              </Popconfirm>
            </div>
          </div>
        ))}
      </div>
    </>
  );

  return (
    <div className={styles.tabInner}>
      <div className={styles.templatesLayout}>
        <div className={`${styles.templatesMainCol} ${styles.sonicPanel}`}>
          {loading ? (
            <div className={styles.centeredSpin}>
              <Spin />
            </div>
          ) : (
            mainContent
          )}
        </div>

        {!loading && templates.length > 0 && (
          <div
            className={`${styles.testCard} ${styles.sonicPanel}`}
            ref={testCardRef}
          >
            <div className={styles.testCardHeader}>
              <span className={styles.testCardTitle}>
                <ThunderboltOutlined /> {t("sonic.test_template_title")}
              </span>
              <Tooltip title={t("sonic.test_template_tooltip")}>
                <InfoCircleOutlined className={styles.testInfoIcon} />
              </Tooltip>
            </div>
            <div className={styles.testCardBody}>
              <label className={styles.fieldLabel}>{t("sonic.select_template")}</label>
              <Select
                className={styles.fullWidth}
                value={testTemplateId}
                onChange={setTestTemplateId}
                placeholder={t("sonic.select_template")}
                options={whatsappTemplates.map((tpl) => ({
                  value: tpl.id,
                  label: tpl.name,
                }))}
                disabled={whatsappTemplates.length === 0}
              />
              {whatsappTemplates.length === 0 && (
                <p className={styles.fieldHelper}>{t("sonic.test_template_whatsapp_only")}</p>
              )}

              <label className={styles.fieldLabel}>{t("sonic.verified_test_number")}</label>
              {numbers.length > 0 ? (
                <Select
                  className={styles.fullWidth}
                  value={testPhone}
                  onChange={setTestPhone}
                  placeholder={t("sonic.verified_test_number")}
                  options={numbers.map((n) => ({
                    value: n.phone,
                    label: n.label ? `${n.label} (${n.phone})` : n.phone,
                  }))}
                />
              ) : (
                <p className={styles.fieldHelper}>{t("sonic.no_verified_numbers")}</p>
              )}

              <Checkbox
                checked={testConsent}
                onChange={(e) => setTestConsent(e.target.checked)}
                className={styles.consentCheck}
              >
                {t("sonic.consent_confirm")}
              </Checkbox>

              <div className={styles.quotaBarWrap}>
                <Progress
                  percent={quotaPct}
                  showInfo={false}
                  strokeColor="var(--primary, #6366f1)"
                />
                <span className={styles.quotaText}>
                  {t("sonic.test_messages_remaining", {
                    remaining: quota.test_calls_remaining,
                    max: quota.test_calls_max,
                  })}
                </span>
              </div>

              <Button
                type="primary"
                block
                loading={testSending}
                disabled={
                  !testTemplateId ||
                  !testPhone ||
                  !testConsent ||
                  quota.test_calls_remaining <= 0
                }
                onClick={sendTestMessage}
              >
                {t("sonic.send_test_message")}
              </Button>
              <p className={styles.sandboxFootnote}>{t("sonic.template_test_footnote")}</p>
            </div>
          </div>
        )}
      </div>

      <Drawer
        title={editing ? t("sonic.edit_template") : t("sonic.new_template")}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={520}
        className={styles.templateDrawer}
        footer={
          <div className={styles.drawerFooter}>
            <Button onClick={() => setDrawerOpen(false)}>{t("sonic.cancel")}</Button>
            <Button type="primary" loading={saving} onClick={save}>
              {t("sonic.save_template")}
            </Button>
          </div>
        }
      >
        <div className={styles.templateDrawerBody}>
          <div className={styles.templateDrawerForm}>
            <div>
              <label className={styles.fieldLabel}>{t("sonic.template_name")}</label>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </div>

            <div>
              <label className={styles.fieldLabel}>{t("sonic.template_language")}</label>
              <Select
                style={{ width: "100%" }}
                value={language}
                onChange={setLanguage}
                options={SUPPORTED_LANGUAGES}
              />
              <p className={styles.fieldHelper}>{t("sonic.template_language_required")}</p>
            </div>

            <div>
              <label className={styles.fieldLabel}>{t("sonic.template_channel")}</label>
              <Segmented
                block
                value={channel}
                onChange={setChannel}
                options={[
                  { value: "whatsapp", label: t("sonic.channel_whatsapp") },
                  { value: "voice", label: t("sonic.template_channel_voice") },
                ]}
              />
            </div>

            {channel === "whatsapp" ? (
              <>
                <div>
                  <label className={styles.fieldLabel}>{t("sonic.template_body")}</label>
                  <TextArea
                    ref={bodyRef}
                    rows={6}
                    value={body}
                    className={styles.variableTextarea}
                    placeholder={t("sonic.template_body_ph")}
                    onChange={(e) => setBody(e.target.value)}
                  />
                  <div className={styles.templateMetaRow}>
                    <span>{t("sonic.template_char_count", { count: charCount })}</span>
                    <span className={styles.metaNotice}>{t("sonic.meta_approval_notice")}</span>
                  </div>
                </div>
                <div>
                  <Button size="small" className={styles.addVarBtn}>
                    + {t("sonic.add_variable")}
                  </Button>
                  <div className={styles.mergeFields}>
                    {WHATSAPP_VARS.map((f) => (
                      <Tag
                        key={f}
                        className={styles.mergeFieldTag}
                        onClick={() => insertMergeField(f)}
                      >
                        {`{{${f}}}`}
                      </Tag>
                    ))}
                    <Tag
                      className={styles.mergeFieldTag}
                      onClick={() => insertMergeField("custom")}
                    >
                      {"{{custom}}"}
                    </Tag>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div>
                  <label className={styles.fieldLabel}>{t("sonic.greeting_script")}</label>
                  <TextArea
                    rows={6}
                    value={body}
                    placeholder={t("sonic.greeting_placeholder")}
                    onChange={(e) => setBody(e.target.value)}
                  />
                  <p
                    className={[
                      styles.speechDurationHint,
                      voiceTooLong ? styles.speechDurationWarn : "",
                    ].join(" ")}
                  >
                    {t("sonic.speech_duration_estimate", {
                      seconds: voiceDuration.seconds,
                      chars: voiceDuration.chars,
                    })}
                    {voiceTooLong && (
                      <span className={styles.speechDurationOver}>
                        {" "}
                        {t("sonic.speech_duration_over_limit", { max: GREETING_MAX_SECONDS })}
                      </span>
                    )}
                  </p>
                </div>
                <div>
                  <label className={styles.fieldLabel}>{t("sonic.tone_persona")}</label>
                  <Select
                    style={{ width: "100%" }}
                    value={persona}
                    onChange={setPersona}
                    options={PERSONA_OPTIONS.map((p) => ({ value: p.value, label: t(p.labelKey) }))}
                  />
                </div>
              </>
            )}
          </div>

          {channel === "whatsapp" && (
            <div className={styles.templatePreviewPanel}>
              <span className={styles.fieldLabel}>{t("sonic.preview")}</span>
              <div className={styles.waPreviewBubble}>{fillPreview(body)}</div>
            </div>
          )}
        </div>
      </Drawer>
    </div>
  );
};

export default TemplatesTab;
